import { type JSX, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import { Button } from 'Components/Buttons/Buttons';
import { Checkbox } from 'Components/Checkbox/Checkbox';
import { Modal } from 'Components/Modal/Modal';
import { Text } from 'Components/Text/Text';
import { ICON_CLOSE, ICON_SAVE } from 'Config/Icons';
import { DueDiligenceQuestion, QuestionnaireContent } from 'Models/DueDiligence';
import { UploadedFile } from 'Models/Files';

import styles from './ManageQuestionFilesModal.module.css';

export interface ManageQuestionFilesModalProps {
    disabled: boolean;
    existingPendingFiles: File[];
    hideModal: () => void;
    initialQuestion: DueDiligenceQuestion;
    initialSelectedPendingFiles: File[];
    lastSavedQuestionnaire: QuestionnaireContent;
    onSave: (newPendingFiles: File[], selectedPendingFiles: File[], selectedUploadedFiles: UploadedFile[]) => void;
}

/** Used for due diligence questionnaires. Allows users to select files that have already been used elsewhere, or to drop in new reusable files. */
export const ManageQuestionFilesModal = (props: ManageQuestionFilesModalProps): JSX.Element => {
    // Files that the user drags and drops that haven't already been used elsewhere in the questionnaire. These will be uploaded when the control group is saved (as long as they are checked for at least one question in the group).
    const [newPendingFiles, setNewPendingFiles] = useState<File[]>([]);
    const [selectedPendingFiles, setSelectedPendingFiles] = useState(props.initialSelectedPendingFiles);
    const [selectedUploadedFiles, setSelectedUploadedFiles] = useState(props.initialQuestion.files);

    const allUploadedFiles: UploadedFile[] = useMemo(() => {
        const files: UploadedFile[] = [];
        const seenIds = new Set<string>();

        props.lastSavedQuestionnaire.control_frameworks.forEach((framework) => {
            framework.control_groups.forEach((group) => {
                group.controls.forEach((control) => {
                    control.questions.forEach((question) => {
                        question.files.forEach((file) => {
                            if (!seenIds.has(file.file_id)) {
                                seenIds.add(file.file_id);
                                files.push(file);
                            }
                        });
                    });
                });
            });
        });

        return files;
    }, [props.lastSavedQuestionnaire]);

    const onDropFiles = (files: File[]) => {
        for (const file of files) {
            const duplicateUploadedFile = allUploadedFiles.find((uploadedFile) => uploadedFile.filename === file.name);
            if (duplicateUploadedFile) {
                if (!selectedUploadedFiles.includes(duplicateUploadedFile)) {
                    setSelectedUploadedFiles((current) => [...current, duplicateUploadedFile]);
                }
            } else {
                const duplicateExistingPendingFile = props.existingPendingFiles.find((pendingFile) => pendingFile.name === file.name);
                const duplicateNewPendingFile = newPendingFiles.find((pendingFile) => pendingFile.name === file.name);

                if (!duplicateExistingPendingFile && !duplicateNewPendingFile) {
                    setNewPendingFiles((current) => [...current, file]);
                    setSelectedPendingFiles((current) => [...current, file]);
                } else if (duplicateNewPendingFile && !selectedPendingFiles.includes(file)) {
                    setSelectedPendingFiles((current) => [...current, file]);
                }
            }
        }
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop: onDropFiles, disabled: props.disabled, multiple: true });
    const disabledClassName = props.disabled ? styles.disabled : '';
    const dragClassName = isDragActive ? styles.dropzoneDragActive : '';

    const checkboxes: JSX.Element = (() => {
        const existingFileValues = allUploadedFiles.map((uploadedFile) => ({
            name: uploadedFile.filename,
            checked: selectedUploadedFiles.map((f) => f.file_id).includes(uploadedFile.file_id),
            onToggled: (isChecked: boolean) => {
                if (isChecked) {
                    setSelectedUploadedFiles((current) => [...current, uploadedFile]);
                } else {
                    setSelectedUploadedFiles((current) => current.filter((f) => f.file_id !== uploadedFile.file_id));
                }
            },
        }));

        const pendingFileValues = [...props.existingPendingFiles, ...newPendingFiles].map((f) => ({
            name: f.name,
            checked: selectedPendingFiles.map((spf) => spf.name).includes(f.name),
            onToggled: (isChecked: boolean) => {
                if (isChecked) {
                    setSelectedPendingFiles((current) => [...current, f]);
                } else {
                    setSelectedPendingFiles((current) => current.filter((currentFile) => currentFile.name !== f.name));
                }
            },
        }));

        return (
            <>
                {[...existingFileValues, ...pendingFileValues]
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((v) => (
                        <Checkbox key={v.name} checked={v.checked} label={v.name} onToggled={v.onToggled} />
                    ))}
            </>
        );
    })();

    return (
        <Modal open header="Manage Supporting Documents" subHeader={'Files that have already been added are available for reuse, or new files can be added in the dotted area.'}>
            <div className={styles.modalContent}>
                <div {...getRootProps({ className: `${styles.dropzone} ${dragClassName} ${disabledClassName}` })}>
                    <input id="fileArea" disabled={props.disabled} {...getInputProps()} />
                    <Text color="darkGray">To add new files, click here or drag and drop here.</Text>
                </div>
                {checkboxes}
            </div>

            <div className="modalFormButtonContainer">
                <Button variant="secondary" onClick={props.hideModal} fontAwesomeImage={ICON_CLOSE}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={() => {
                        props.onSave(newPendingFiles, selectedPendingFiles, selectedUploadedFiles);
                        props.hideModal();
                    }}
                    fontAwesomeImage={ICON_SAVE}
                >
                    Save
                </Button>
            </div>
        </Modal>
    );
};
